/**
 * Remembers the article feed between visits.
 *
 * The feed is an endless scroll (10 articles per page). When a reader opens one
 * article and comes back, React remounts the page and would fetch page 1 again —
 * throwing away every article that was already loaded and jumping to the top.
 * This module keeps the loaded list, the page number, the active filters, the
 * “random” seed and the reading position, so coming back is instant and the
 * reader can simply **keep scrolling**.
 *
 * Kept in memory (survives SPA navigation = browser Back) and mirrored into
 * sessionStorage (survives a reload / F5 in the same tab; a new tab starts
 * fresh). Scroll tracking stays in memory only — writing the whole list on every
 * scroll event would be wasteful, so it is flushed when the tab is hidden.
 */
const KEY = 'ms_feed_state';

const EMPTY = {
  key: '', posts: [], page: 1, pages: 1, total: 0, allTotal: 0,
  sort: 'random', scope: 'all', seed: 0, scrollY: 0,
};

function fromStorage() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : { ...EMPTY };
  } catch (e) {
    return { ...EMPTY };
  }
}

let state = fromStorage();
let flush = null;

function flushSoon() {
  if (flush) return;
  flush = setTimeout(() => {
    flush = null;
    try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* full / private */ }
  }, 400);
}

/** The list/filters the reader had — call it on the first render of the feed. */
export const readFeed = () => state;

/**
 * Merge a patch into the cached feed.
 * @param {object} patch
 * @param {boolean} [persist] false = memory only (scroll position).
 */
export function saveFeed(patch, persist = true) {
  state = { ...state, ...patch };
  if (persist) flushSoon();
  return state;
}

/** Drop everything (a different filter set owns the screen now). */
export function resetFeed() {
  state = { ...EMPTY };
  try { sessionStorage.removeItem(KEY); } catch (e) { /* ignore */ }
  return state;
}

if (typeof window !== 'undefined') {
  const store = () => {
    try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
  };
  window.addEventListener('pagehide', store);          // leaving / closing the tab
  document.addEventListener('visibilitychange', () => { if (document.hidden) store(); });
}
